// Token storage + refresh helpers shared between AuthShell's in-app flows
// and the rest of the app (api.ts `j()` 401 retry, SSE stream, etc.).
// Hosted-UI / PKCE redirect flow is gone; see AuthShell.tsx and
// cognitoClient.ts for the live auth surface.

// Build-time configurable so each Pulumi stack ships an SPA bundle pointed
// at its own Cognito user pool. deploy.py --frontend injects VITE_COGNITO_*
// from the stack outputs; the literals below are the dev-stack defaults
// for `pnpm dev:web` against local dev.
const COGNITO_DOMAIN =
  import.meta.env.VITE_COGNITO_DOMAIN ??
  "law-agent-dev.auth.ap-southeast-2.amazoncognito.com";
const CLIENT_ID =
  import.meta.env.VITE_COGNITO_CLIENT_ID ?? "63rpfpibfvaq68qei7k41id1i5";

const TOKEN_KEY = "cognito_id_token";
const REFRESH_KEY = "cognito_refresh_token";

export function getIdToken(): string | null {
  return localStorage.getItem(TOKEN_KEY);
}

let refreshInFlight: Promise<string | null> | null = null;

export async function refreshIdToken(): Promise<string | null> {
  if (refreshInFlight) return refreshInFlight;
  const refreshToken = localStorage.getItem(REFRESH_KEY);
  if (!refreshToken) return null;

  refreshInFlight = (async () => {
    try {
      const body = new URLSearchParams({
        grant_type: "refresh_token",
        client_id: CLIENT_ID,
        refresh_token: refreshToken,
      });
      const res = await fetch(`https://${COGNITO_DOMAIN}/oauth2/token`, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body,
      });
      if (!res.ok) {
        localStorage.removeItem(TOKEN_KEY);
        localStorage.removeItem(REFRESH_KEY);
        return null;
      }
      const tokens = await res.json();
      if (tokens.id_token) {
        localStorage.setItem(TOKEN_KEY, tokens.id_token);
      }
      return tokens.id_token ?? null;
    } finally {
      refreshInFlight = null;
    }
  })();
  return refreshInFlight;
}

export function signOut() {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(REFRESH_KEY);
  window.location.href = "/";
}

export function parseIdToken(token: string): { email?: string; sub: string; username?: string; groups?: string[] } | null {
  try {
    const payload = token.split(".")[1];
    const json = atob(payload.replace(/-/g, "+").replace(/_/g, "/"));
    const parsed = JSON.parse(json);
    parsed.groups = parsed["cognito:groups"] ?? [];
    // Cognito emits the Cognito-side username under "cognito:username"; the
    // admin-user list keys rows by that value, so surface it as `username`
    // here for easy comparison with AdminUser.username.
    if (!parsed.username && parsed["cognito:username"])
      parsed.username = parsed["cognito:username"];
    return parsed;
  } catch {
    return null;
  }
}

export function getUserRole(): "admin" | "user" | "unconfirmed" | null {
  const token = getIdToken();
  if (!token) return null;
  const parsed = parseIdToken(token);
  if (!parsed) return null;
  const groups = parsed.groups ?? [];
  if (groups.includes("admin")) return "admin";
  if (groups.includes("user")) return "user";
  if (groups.includes("unconfirmed")) return "unconfirmed";
  return null;
}
